// Pure helpers for the session summary surface (post-save card on mobile,
// read-only detail pane on the web console). Moved here from
// apps/mobile/src/logger/postSessionSummaryMath.ts so the headline-pick +
// duration/density formatting are the single source for both mounts.
import { formatWeight, type WeightUnitLabels } from "./units.js";
import type { ExerciseResult, SessionResult } from "./schemas/sessionResult.js";

// Pick the highest-delta E1RM PR (sticky flag from SessionResult). Ties
// broken by exercise volume so we prefer the headline-worthy lift.
export const pickHeadlinePR = (
  results: readonly ExerciseResult[],
): ExerciseResult | null => {
  let best: ExerciseResult | null = null;
  for (const er of results) {
    if (!er.isPrE1rm) continue;
    if (er.topE1rmKg === null) continue;
    if (!best || (er.deltaE1rmKg ?? 0) > (best.deltaE1rmKg ?? 0)) {
      best = er;
    }
  }
  return best;
};

export const formatDuration = (minutes: number | null): string => {
  if (minutes === null || !Number.isFinite(minutes) || minutes <= 0) {
    return "—";
  }
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

// Whole-kcal formatter (HC-8) — energy is never shown fractional. Same
// null/NaN/negative guard shape as formatDuration/formatDensity; 0 is a real
// value (a bodyweight-only session), only absent/invalid collapses to "—".
export const formatKcal = (kcal: number | null): string => {
  if (kcal === null || !Number.isFinite(kcal) || kcal < 0) return "—";
  return `${Math.round(kcal)} kcal`;
};

// Density formats with no decimals — at family-scale the kg/min number
// is usually 80–250 and decimals add noise without precision.
export const formatDensity = (
  density: number | null,
  unit: "kg" | "lb",
  locale: string,
  labels: WeightUnitLabels,
): string => {
  if (density === null || !Number.isFinite(density) || density <= 0) {
    return "—";
  }
  return `${formatWeight(density, unit, locale, labels)}/min`;
};

// Which headline to show — extracted from SessionSummaryView (RW-2/RW-17) so
// it's testable without a component harness. Returns a DECISION, not a
// formatted string: the caller still owns i18n + unit formatting (name
// resolution, t(), formatWeight/formatE1rm all need render-time context this
// module doesn't have).
//
// Before this change, a session with zero weighted volume (cardio/timed-only
// — kind="cardio" contributes neither volume nor setCount, by design, see
// setMetrics.ts) always fell through to the "fallback" branch and its
// "{{sets}} sets" wording, which read as "0 sets" even though the athlete
// really did log a set (Antenore's report, RW-17: a 759s walking bout showing
// "SESSION — 0 sets"). "solidDuration" catches exactly that case: no weighted
// volume, but the session genuinely took time.
export type HeadlineDecision =
  | { kind: "pr"; exercise: ExerciseResult }
  | { kind: "delta"; exercise: ExerciseResult }
  | { kind: "solidVolume"; totalVolumeKg: number; durationMinutes: number }
  | { kind: "solidDuration"; durationMinutes: number }
  | { kind: "fallback"; totalSets: number };

export const pickHeadlineDecision = (
  result: SessionResult,
): HeadlineDecision => {
  const headlinePR = pickHeadlinePR(result.exerciseResults);
  if (headlinePR && headlinePR.topE1rmKg !== null) {
    return { kind: "pr", exercise: headlinePR };
  }
  const bigDelta = result.exerciseResults.find(
    (er) =>
      er.deltaE1rmKg !== null && er.topE1rmKg !== null && er.deltaE1rmKg > 5,
  );
  if (bigDelta) {
    return { kind: "delta", exercise: bigDelta };
  }
  const hasDuration =
    result.durationMinutes !== null && result.durationMinutes > 0;
  if (result.totalVolumeKg > 0 && hasDuration) {
    return {
      kind: "solidVolume",
      totalVolumeKg: result.totalVolumeKg,
      durationMinutes: result.durationMinutes as number,
    };
  }
  if (hasDuration) {
    return {
      kind: "solidDuration",
      durationMinutes: result.durationMinutes as number,
    };
  }
  const totalSets = result.exerciseResults.reduce(
    (acc, er) => acc + er.setCount,
    0,
  );
  return { kind: "fallback", totalSets };
};
